import React, { useRef, useState } from "react";
import TableHead from "@material-ui/core/TableHead";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import CustomDatePicker from "../../../../../app/components/custom.date.picker";
import { TextField } from "@material-ui/core";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import { makeStyles } from "@material-ui/styles";
import ToolTip from "../../../../../app/components/tooltip";
import { toParticipantUiText } from "../../../../../app/utils/participantTerminology";

// Locks (disables) date/comment editing only while the remodel-set date is still
// before the remodel date (the date the remodel plan was created). Falls back to
// Date.now() when the caller can't resolve the real remodel date.
export const isRemodelLockedMilestone = (row, remodelDateMillis = Date.now()) =>
  /set to (?:actual(?: \(or plan\))?|plan) date via remodel/i.test(
    row.comments || "",
  ) &&
  typeof row.milestoneDateMillis === "number" &&
  row.milestoneDateMillis < remodelDateMillis;

const POST_FPE_MILESTONE_IDS = new Set([146, 148, 149, 151, 152]);

export const isPostFpeMilestone = (row) =>
  POST_FPE_MILESTONE_IDS.has(row.milestoneId);

// isLocked defaults to a live check but callers should pass the lock status
// captured at mount, so later edits to the date/comments can't re-lock the row.
export const isMilestoneRowEditable = (
  row,
  { isOverrideActive, isLocked = isRemodelLockedMilestone(row) },
) => {
  if (isLocked) return false;
  if (isPostFpeMilestone(row)) return false;
  return isOverrideActive;
};

// A row is only pre-checked/editable if it has actually been overridden;
// Remodel alone never implies an override.
export const getDefaultOverrideActive = (row) => !!row.isUserOverride;

const OVERRIDE_DISABLED_MILESTONE_NAMES = [
  "DURATION_TO_FPC",
  "DURATION_TO_FPLV",
  "DURATION_TO_LPI",
  "DURATION_TO_LPLV",
  "DURATION_TO_TFED",
];

const cellData = [
  { width: "25%", text: "Milestone Name" },
  {
    width: "15%",
    text: "Override",
    tooltip: "Check to confirm that calculated date will be overwritten.",
  },
  { width: "25%", text: "Milestone Date" },
  { width: "35%", text: "Comments" },
];

const CountryMilestoneTable = ({
  rows,
  handleChange,
  isRightOrLeft = null,
  remodelDateMillis,
}) => {
  const classes = useStyles();
  const inputClasses = useTextFieldStyles();

  const originalDates = useRef(
    rows.reduce((acc, row) => {
      acc[row.milestoneName] = row.milestoneDateMillis;
      return acc;
    }, {}),
  );

  // Snapshot the remodel-locked status once per row so it can't flip back to
  // locked just because the user edited the date/comments after unlocking it.
  const initialLockedMilestones = useRef(
    rows.reduce((acc, row) => {
      acc[row.milestoneName] = isRemodelLockedMilestone(row, remodelDateMillis);
      return acc;
    }, {}),
  );

  const [isActiveCells, setIsActiveCells] = useState(
    rows.reduce((acc, row) => {
      acc[row.milestoneName] = getDefaultOverrideActive(row);
      return acc;
    }, {}),
  );

  const getMinAllowedDate = (index) => {
    if (index < 1) return null;

    const prevDates = rows
      .slice(1, index)
      .map((row) => row.milestoneDateMillis);

    if (prevDates.length === 0) return null;

    const maxPrevDate = new Date(Math.max(...prevDates));
    maxPrevDate.setDate(maxPrevDate.getDate());

    return maxPrevDate;
  };

  const isRowEditable = (row) =>
    isMilestoneRowEditable(row, {
      isOverrideActive: isActiveCells[row.milestoneName],
      isLocked: initialLockedMilestones.current[row.milestoneName],
    });

  const handleActiveCells = (milestoneName, value) => {
    setIsActiveCells((prevState) => ({
      ...prevState,
      [milestoneName]: value,
    }));

    if (!value) {
      handleChange(milestoneName, "comments", "");

      handleChange(
        milestoneName,
        "milestoneDateMillis",
        originalDates.current[milestoneName],
      );
    }
  };

  const handleCheckboxChange = (e, row) => {
    if (initialLockedMilestones.current[row.milestoneName]) return;

    const milestoneName = row.milestoneName;
    const newValue = e.target.checked ? 1 : 0;
    handleActiveCells(milestoneName, e.target.checked);
    handleChange(milestoneName, "isUserOverride", newValue);
  };

  // Modify the cellData based on isRightOrLeft
  const modifiedCellData =
    isRightOrLeft === "right"
      ? cellData.filter((cell) => cell.text !== "Override")
      : cellData;

  return (
    <TableContainer>
      <Table
        aria-label="customized table"
        className="table-container"
      >
        <TableHead className={classes.tableHead}>
          <TableRow>
            {modifiedCellData.map(({ width, text, tooltip }, index) => (
              <TableCell
                key={index}
                className={classes.tableCell}
                style={{ width }}
              >
                <div
                  style={
                    tooltip
                      ? { display: "flex", alignItems: "center" }
                      : undefined
                  }
                >
                  <strong>{text}</strong>
                  {tooltip && <ToolTip info={tooltip} />}
                </div>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows?.map((row, index) => (
            <TableRow key={index}>
              <TableCell
                className={classes.tableCell}
                component="th"
                scope="row"
              >
                {toParticipantUiText(row.milestoneLabel)}
              </TableCell>

              {isRightOrLeft !== "right" && (
                <TableCell className={classes.checkBoxCell}>
                  <FormControlLabel
                    style={{ margin: "0" }}
                    control={
                      <Checkbox
                        style={{
                          color: "var(--teal-600)",
                        }}
                        checked={row.isUserOverride === 1}
                        onChange={(e) => handleCheckboxChange(e, row)}
                        name="isUserOverride"
                        disabled={
                          initialLockedMilestones.current[row.milestoneName] ||
                          OVERRIDE_DISABLED_MILESTONE_NAMES.includes(
                            row.milestoneName,
                          )
                        }
                      />
                    }
                  />
                </TableCell>
              )}

              <TableCell
                className={
                  isRowEditable(row)
                    ? classes.tableCell
                    : classes.disabledTableCell
                }
                component="th"
                scope="row"
              >
                <CustomDatePicker
                  label=""
                  value={row.milestoneDateMillis}
                  minDate={getMinAllowedDate(index)}
                  onChange={(value) => {
                    const normalizedDate = new Date(value);
                    normalizedDate.setUTCHours(12, 0, 0, 0); // Set to noon UTC
                    handleChange(
                      row.milestoneName,
                      "milestoneDateMillis",
                      normalizedDate.getTime(),
                      row.milestoneDateMillis,
                    );
                  }}
                  showYearDropdown
                  showMonthDropdown
                  useShortMonthInDropdown
                  dateFormat={"dd/MMM/yyyy"}
                  disabled={!isRowEditable(row)}
                />
              </TableCell>

              <TableCell
                className={
                  isRowEditable(row)
                    ? classes.tableCell
                    : classes.disabledTableCell
                }
                component="th"
                scope="row"
              >
                <TextField
                  style={{ width: "100%" }}
                  label="Comments"
                  variant="outlined"
                  value={row.comments}
                  onChange={(e) =>
                    handleChange(row.milestoneName, "comments", e.target.value)
                  }
                  InputProps={{ classes: inputClasses }}
                  disabled={!isRowEditable(row)}
                  required
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CountryMilestoneTable;

const useStyles = makeStyles((theme) => ({
  root: {
    color: "#ed1a3c",
    fontWeight: 600,
  },
  tableContainer: {
    textAlign: "center",
    border: "1px solid var(--surface-300)",
  },
  tableHead: {
    background: "var(--surface-50)",
    border: "1px solid var(--surface-300)",
    textAlign: "center",
  },
  tableCell: {
    border: "1px solid var(--surface-300)",
    textAlign: "left",
    padding: "5px",
    paddingBlock: "0.8em",
  },
  disabledTableCell: {
    pointerEvents: "none",
    userSelect: "none",
    border: "1px solid var(--surface-300)",
    paddingBlock: "0.8em",
    padding: "5px",
  },
  checkBoxCell: {
    border: "1px solid var(--surface-300)",
    textAlign: "center",
    position: "relative",
    padding: "5px",
  },
}));

const useTextFieldStyles = makeStyles((theme) => ({
  input: {
    background: "white",
  },
}));
